/**
 * Type-safe analytics event catalog.
 * All PostHog event names should be defined here for consistency.
 */

export const AnalyticsEvents = {
  // Auth
  AUTH_SIGN_IN: 'auth_sign_in',
  AUTH_SIGN_OUT: 'auth_sign_out',
  AUTH_SIGN_IN_FAILED: 'auth_sign_in_failed',

  // Entries
  ENTRY_CREATED: 'entry_created',
  ENTRY_UPDATED: 'entry_updated',
  ENTRY_DELETED: 'entry_deleted',

  // Navigation
  PAGE_VIEW: '$pageview',

  // Reports
  REPORT_VIEWED: 'report_viewed',
  REPORT_EXPORTED: 'report_exported',

  // Admin
  USER_INVITED: 'user_invited',
  USER_UPDATED: 'user_updated',
  USER_DELETED: 'user_deleted',
  API_KEY_CREATED: 'api_key_created',
  SETTINGS_UPDATED: 'settings_updated',

  // Changelog
  CHANGELOG_VIEWED: 'changelog_viewed',
  CHANGELOG_PUBLISHED: 'changelog_published',

  // Voice Entry
  VOICE_ENTRY_STARTED: 'voice_entry_started',
  VOICE_ENTRY_COMPLETED: 'voice_entry_completed',
  VOICE_ENTRY_CANCELLED: 'voice_entry_cancelled',
  VOICE_ENTRY_RERECORD: 'voice_entry_rerecord',
  VOICE_ENTRY_ERROR: 'voice_entry_error',
  VOICE_ENTRY_SUBMITTED: 'voice_entry_submitted',
  VOICE_SUGGESTIONS_ACCEPTED: 'voice_suggestions_accepted',
  VOICE_SUGGESTIONS_DISCARDED: 'voice_suggestions_discarded',

  // Data-loss telemetry (DEV-128)
  VOICE_ENTRY_MAX_DURATION_REACHED: 'voice_entry_max_duration_reached',
  VOICE_ENTRY_PROCESSING_ABORTED: 'voice_entry_processing_aborted',
  VOICE_ENTRY_APP_BACKGROUNDED: 'voice_entry_app_backgrounded',
  VOICE_ENTRY_FIELD_DROPPED_LOW_CONFIDENCE: 'voice_entry_field_dropped_low_confidence',
  VOICE_RECORDING_ORPHANED: 'voice_recording_orphaned',
  ENTRY_FORM_CLEARED_WITH_VOICE_DATA: 'entry_form_cleared_with_voice_data',
  TEXT_CLEANUP_DISCARDED_ORIGINAL: 'text_cleanup_discarded_original',

  // Live Map
  LIVE_MAP_VIEWED: 'live_map_viewed',
  PRACTICE_MAP_VIEWED: 'practice_map_viewed',
  LIVE_MAP_DEVICE_CLICKED: 'live_map_device_clicked',
  LIVE_MAP_SEARCH_USED: 'live_map_search_used',
  LIVE_MAP_DISPLAY_MODE_LOADED: 'live_map_display_mode_loaded',

  // App lifecycle
  APP_RELOAD: 'app_reload',

  // Customers (customer_profiles feature)
  CUSTOMER_CREATED: 'customer_created',
  CUSTOMER_UPDATED: 'customer_updated',
  CUSTOMER_DEACTIVATED: 'customer_deactivated',
  CUSTOMER_REACTIVATED: 'customer_reactivated',
  CUSTOMER_BUSINESS_CARD_UPLOADED: 'customer_business_card_uploaded',
  CUSTOMER_BUSINESS_CARD_REMOVED: 'customer_business_card_removed',
} as const

export type AnalyticsEvent = (typeof AnalyticsEvents)[keyof typeof AnalyticsEvents]

/** Properties for entry-related events */
export interface EntryEventProperties {
  entry_id?: string
  activity_id?: string
  customer_id?: string
}

/** Properties for report events */
export interface ReportEventProperties {
  report_type: string
  date_range?: string
  format?: string
}

/** Properties for admin events */
export interface AdminEventProperties {
  target_user_id?: string
  target_user_email?: string
  change_type?: string
}

/** Properties for voice entry error events */
export interface VoiceEntryErrorProperties {
  /** Which stage of the pipeline failed */
  error_stage: 'mic_access' | 'network_offline' | 'network_lost' | 'transcription' | 'max_retries'
  /** The error message shown to the user */
  error_message: string
  /** Current retry attempt (0 = first try) */
  retry_count: number
  /** Whether an audio blob existed at time of error */
  had_audio_blob: boolean
  /** Recording duration in ms (0 if mic never started) */
  recording_duration_ms: number
  /** Error constructor name for transcription errors (e.g., 'FunctionsHttpError') */
  error_type?: string
  /** Response body from edge function on failure */
  response_body?: string
}

/** Properties for APP_RELOAD event */
export interface AppReloadEventProperties {
  /** What caused this cold boot */
  cause: 'capgo_update' | 'cold_start'
  /** Current Capgo bundle version */
  bundle_version: string
  /** Previous Capgo bundle version from last boot, or null on first boot */
  previous_bundle_version: string | null
}
